import {
  IconArrowBack,
  IconArrowRight,
  IconDots,
  IconMail,
  IconPencil,
  IconRefresh,
  IconTag,
  IconTrash,
} from '@tabler/icons-react';
import { BenefitStatus, BenefitType } from '@zorro/clients';
import { getFullName } from '@zorro/common-utils';
import { ActionIcon } from '@zorro/design-system/v2/ActionIcon';
import { Menu } from '@zorro/design-system/v2/Menu';
import { Modal } from '@zorro/design-system/v2/Modal';
import {
  useAppRouter,
  useMonolithMutation,
  useMonolithQuery,
  useRoles,
} from '@zorro/web/ui-utils';
import { useState } from 'react';

import { CarrierApplicationModalStepType } from '../../../../../CarrierApplicationFlow';
import { EnrollmentConfirmationForm } from '../../../../../EmployeeActions/EmailCommunications/EnrollmentConfirmationForm';
import { EnrollmentConfirmationModal } from '../../../../../EnrollmentConfirmationFlow';
import { EditEnrollmentTags } from '../../../../../EnrollmentTags';
import { UndoApplicationForm } from '../../../../../UndoApplicationForm';
import { UndoEnrollmentConfirmationForm } from '../../../../../UndoEnrollmentConfirmationForm';
import { EnrollmentAction } from '../../../../EmployeePageHeader';
import { EmployeePagePlansBenefitCardPlanElectionDrawer } from '../EmployeePagePlansBenefitCardPlanElectionDrawer';
import {
  EmployeePageBenefitCardMenuAction,
  getMenuConfig,
} from './EmployeePagePlansBenefitCardActionsMenuUtils';

type Props = {
  benefitId: string;
  employeeId: string;
  employerId: string;
  onboardingPeriodId: string;
  benefitType: BenefitType;
  isDon: boolean;
  status?: BenefitStatus | null;
  onEnrollmentTabActionChange: (action?: EnrollmentAction) => void;
};
export const EmployeePagePlansBenefitCardActionsMenu = ({
  onboardingPeriodId,
  benefitType,
  employeeId,
  employerId,
  benefitId,
  status,
  isDon,
  onEnrollmentTabActionChange,
}: Props) => {
  const [action, setAction] = useState<EmployeePageBenefitCardMenuAction>();
  const [open, setOpen] = useState(false);
  const { navigateToRouteAsync } = useAppRouter();
  const { isManagement } = useRoles();

  const canEditTags = isManagement && benefitType === BenefitType.MAJOR_MEDICAL;

  const { tryMutate: sendEnrollmentConfirmationEmail } = useMonolithMutation({
    method: 'enrollmentCommunicationsControllerSendEnrollmentConfirmationEmail',
    successMessage: 'Email sent',
  });

  const { data: onboardingPeriod } = useMonolithQuery({
    method: 'onboardingPeriodReportsControllerGenerateOnboardingPeriodReport',
    params: [onboardingPeriodId],
  });
  const { data: benefits } = useMonolithQuery({
    method: 'benefitsControllerGetBenefits',
    params: [onboardingPeriodId],
  });
  const { data: employee } = useMonolithQuery({
    method: 'employeesControllerFindOne',
    params: [employeeId],
  });

  const menuConfig = getMenuConfig(status, benefits, benefitType, isDon);

  if (!menuConfig || !onboardingPeriod || !employee) {
    return null;
  }

  const {
    withEdit,
    withRemove,
    withUndoApplication,
    withChangeApplicationDetails,
    withUndoConfirmation,
    withChangeEnrollmentDetails,
    withSendConfirmationEmail,
    withReportApplicationSent,
  } = menuConfig;

  const handleReportApplicationSent = async () => {
    await navigateToRouteAsync(
      'OPS_EMPLOYEE_PLANS_TAB',
      [{ onboardingPeriodId, employerId, employeeId }],
      { benefitId }
    );
    onEnrollmentTabActionChange(EnrollmentAction.CARRIER_APPLICATION);
  };

  const handleChangeApplicationDetails = async () => {
    await navigateToRouteAsync(
      'OPS_EMPLOYEE_PLANS_TAB',
      [{ onboardingPeriodId, employerId, employeeId }],
      {
        benefitId,
        step: CarrierApplicationModalStepType.ENROLLMENT_INFO,
      }
    );
    onEnrollmentTabActionChange(EnrollmentAction.CARRIER_APPLICATION);
  };
  const handleCloseClick = () => setAction(undefined);

  return (
    <>
      <Menu opened={open} onChange={setOpen} size="md" position="bottom">
        <Menu.Target>
          <ActionIcon
            bd="1px solid var(--color-border-default-primary)"
            isChecked={open}
          >
            <IconDots color="var(--color-icon-fill-on-secondary)" />
          </ActionIcon>
        </Menu.Target>
        <Menu.Dropdown>
          <Menu.Label>Plan actions</Menu.Label>

          {withEdit && (
            <Menu.Item
              onClick={() =>
                setAction(EmployeePageBenefitCardMenuAction.EDIT_PLAN)
              }
              leftSection={<IconPencil size={16} stroke={1.25} />}
            >
              Edit plan
            </Menu.Item>
          )}
          {withReportApplicationSent && (
            <Menu.Item
              onClick={handleReportApplicationSent}
              leftSection={<IconArrowRight size={16} stroke={1.25} />}
            >
              Report application sent
            </Menu.Item>
          )}
          {withRemove && (
            <Menu.Item
              onClick={() =>
                setAction(EmployeePageBenefitCardMenuAction.REMOVE_PLAN)
              }
              leftSection={<IconTrash size={16} stroke={1.25} />}
            >
              Remove plan
            </Menu.Item>
          )}
          {withUndoApplication && (
            <Menu.Item
              onClick={() =>
                setAction(EmployeePageBenefitCardMenuAction.UNDO_APPLICATION)
              }
              leftSection={<IconArrowBack size={16} stroke={1.25} />}
            >
              Undo application
            </Menu.Item>
          )}
          {withChangeApplicationDetails && (
            <Menu.Item
              onClick={handleChangeApplicationDetails}
              leftSection={<IconRefresh size={16} stroke={1.25} />}
            >
              Change application details
            </Menu.Item>
          )}
          {withUndoConfirmation && (
            <Menu.Item
              onClick={() =>
                setAction(EmployeePageBenefitCardMenuAction.UNDO_CONFIRMATION)
              }
              leftSection={<IconArrowBack size={16} stroke={1.25} />}
            >
              Undo enrollment confirmation
            </Menu.Item>
          )}
          {withChangeEnrollmentDetails && (
            <Menu.Item
              onClick={async () => {
                await navigateToRouteAsync(
                  'OPS_EMPLOYEE_PLANS_TAB',
                  [{ onboardingPeriodId, employerId, employeeId }],
                  { benefitId }
                );
                setAction(
                  EmployeePageBenefitCardMenuAction.CHANGE_ENROLLMENT_DETAILS
                );
              }}
              leftSection={<IconRefresh size={16} stroke={1.25} />}
            >
              Change enrollment details
            </Menu.Item>
          )}
          {withSendConfirmationEmail && (
            <Menu.Item
              onClick={() =>
                sendEnrollmentConfirmationEmail([onboardingPeriodId])
              }
              leftSection={<IconMail size={16} stroke={1.25} />}
            >
              Send enrollment confirmation email
            </Menu.Item>
          )}
          {canEditTags && (
            <Menu.Sub>
              <Menu.Sub.Target>
                <Menu.Sub.Item
                  leftSection={<IconTag size={16} stroke={1.25} />}
                >
                  Edit tags
                </Menu.Sub.Item>
              </Menu.Sub.Target>
              <Menu.Sub.Dropdown>
                <EditEnrollmentTags
                  records={[
                    {
                      onboardingPeriodId,
                      fullName: getFullName(employee),
                      enrollmentTags: onboardingPeriod.enrollmentTags,
                    },
                  ]}
                  onClose={() => setOpen(false)}
                />
              </Menu.Sub.Dropdown>
            </Menu.Sub>
          )}
        </Menu.Dropdown>
      </Menu>

      <EmployeePagePlansBenefitCardPlanElectionDrawer
        isOpen={action === EmployeePageBenefitCardMenuAction.EDIT_PLAN}
        onboardingPeriodId={onboardingPeriodId}
        onClose={handleCloseClick}
        benefitType={benefitType}
        employeeId={employeeId}
        benefitId={benefitId}
      />

      <Modal
        isOpen={action === EmployeePageBenefitCardMenuAction.REMOVE_PLAN}
        onClose={handleCloseClick}
        title="Remove plan"
        size="lg"
      >
        Remove plan
      </Modal>

      <Modal
        size="md"
        onClose={handleCloseClick}
        isOpen={action === EmployeePageBenefitCardMenuAction.UNDO_APPLICATION}
        title="Undo application?"
      >
        <UndoApplicationForm benefitId={benefitId} onClose={handleCloseClick} />
      </Modal>

      <Modal
        isOpen={action === EmployeePageBenefitCardMenuAction.UNDO_CONFIRMATION}
        onClose={handleCloseClick}
        title="Undo enrollment confirmation?"
        size="lg"
      >
        <UndoEnrollmentConfirmationForm
          benefitId={benefitId}
          onClose={handleCloseClick}
        />
      </Modal>

      <EnrollmentConfirmationModal
        isOpen={
          action === EmployeePageBenefitCardMenuAction.CHANGE_ENROLLMENT_DETAILS
        }
        onClose={handleCloseClick}
        onboardingPeriodId={onboardingPeriodId}
        employerId={employerId}
        employeeId={employeeId}
      />

      {employee && (
        <Modal
          isOpen={
            action === EmployeePageBenefitCardMenuAction.ENROLLMENT_CONFIRMATION
          }
          onClose={handleCloseClick}
          title={`Send enrollment confirmation email to ${getFullName(employee)}?`}
          size="lg"
        >
          <EnrollmentConfirmationForm
            recordsForActions={[{ onboardingPeriodId }]}
            onSuccess={handleCloseClick}
            onClose={handleCloseClick}
          />
        </Modal>
      )}
    </>
  );
};
